import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Download, Building2, Home, MapPin, Vote, Upload, FileSpreadsheet, Loader2, Info } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { usePagedEntity } from '../hooks/useEntity';
import { dptApi, dptExcelApi, statsApi, desaApi } from '../lib/api';
import { toast } from '../hooks/use-toast';
import EntityFormDialog from '../components/EntityFormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { Pager } from '../components/shared/UI';
import { KECAMATAN_LIST } from '../mock/mockData';
import { useAuth } from '../context/AuthContext';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';

// Daftar Desa/Kelurahan resmi per kecamatan (dari /api/wilayah/desa) - dipakai
// supaya field Desa jadi dropdown yang otomatis mengikuti Kecamatan yang
// dipilih, bukan ketik manual.
const useDesaMap = () => {
  const [desaMap, setDesaMap] = useState({});
  useEffect(() => {
    desaApi.list().then(r => setDesaMap(Object.fromEntries(r.data.map(d => [d.kecamatan, d.desa])))).catch(() => {});
  }, []);
  return desaMap;
};

const buildDptFields = (desaMap) => [
  { type: 'section', label: 'Data Pemilih' },
  { name: 'nama', label: 'Nama Lengkap', required: true, placeholder: 'Nama Pemilih sesuai DPT' },

  { type: 'section', label: 'Wilayah & TPS' },
  { name: 'kecamatan', label: 'Kecamatan', required: true, type: 'select', options: KECAMATAN_LIST.map(k => ({ value: k.name, label: k.name })) },
  { name: 'desa', label: 'Desa / Kelurahan', required: true, type: 'select', placeholder: 'Pilih Kecamatan dulu',
    options: (data) => (desaMap[data.kecamatan] || []).map(d => ({ value: d.nama, label: d.nama })) },
  { name: 'rw', label: 'RW' },
  { name: 'rt', label: 'RT' },
  { name: 'tps', label: 'No. TPS', placeholder: 'TPS 01' },
];

const Dpt = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const canWrite = isSuperAdmin;
  const desaMap = useDesaMap();
  const FIELDS = buildDptFields(desaMap);
  const [search, setSearch] = useState('');
  const [filterKec, setFilterKec] = useState('all');
  const { items, total, page, pages, limit, loading, setPage, create, update, remove, load } = usePagedEntity(dptApi, 'DPT', {
    search, filters: { kecamatan: filterKec !== 'all' ? filterKec : undefined },
  });
  const [wilayahStats, setWilayahStats] = useState(null);
  const [kecList, setKecList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef();

  const loadStats = () => {
    if (!isSuperAdmin) return;
    dptApi.statsWilayah().then(r => setWilayahStats(r.data)).catch(() => {});
    statsApi.kecamatanDetail().then(r => setKecList(r.data.map(k => k.kecamatan).filter(Boolean).sort())).catch(() => {});
  };
  useEffect(() => { loadStats(); }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center bg-white rounded-2xl card-shadow border border-gray-100 max-w-lg mx-auto my-12">
        <div className="w-16 h-16 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
          <Vote className="w-8 h-8" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Akses Terbatas: Data DPT</h3>
        <p className="text-sm text-gray-500 font-medium leading-relaxed">
          Halaman dan master data pemilih DPT hanya dapat diakses, dilihat, dan dikelola oleh Super Admin demi keamanan dan privasi data pemilih.
        </p>
      </div>
    );
  }

  const handleSubmit = async (data) => {
    if (editData) return await update(editData.id, data);
    return await create(data);
  };

  const downloadTemplate = async () => {
    try {
      const res = await dptExcelApi.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'template_dpt.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Template terunduh' });
    } catch (e) { toast({ title: 'Gagal unduh', variant: 'destructive' }); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await dptExcelApi.export();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'export_dpt.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Data DPT berhasil diexport' });
    } catch (e) { toast({ title: 'Gagal export data', variant: 'destructive' }); }
    finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await dptExcelApi.import(file);
      const { inserted, updated, errors, total_errors } = res.data;
      toast({
        title: `Import selesai — ${inserted} baru, ${updated} diperbarui`,
        description: total_errors > 0 ? `${total_errors} baris gagal (cek konsol)` : 'Semua baris berhasil'
      });
      if (errors?.length) console.warn('Import errors:', errors);
      await load();
      loadStats();
    } catch (err) {
      toast({ title: 'Gagal import', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setImporting(false); e.target.value = ''; }
  };

  const handleDeleteAll = async () => {
    try {
      const res = await dptApi.deleteAll();
      toast({ title: `${res.data.deleted} data DPT dihapus` });
      await load();
      loadStats();
    } catch (e) {
      toast({ title: 'Gagal menghapus', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const stats = [
    { l: 'Total DPT', v: wilayahStats?.total_dpt ?? total, icon: Vote, c: 'text-orange-600', bg: 'bg-orange-50' },
    { l: 'Kecamatan', v: wilayahStats?.kecamatan ?? 0, icon: Building2, c: 'text-amber-600', bg: 'bg-amber-50' },
    { l: 'Desa / Kelurahan', v: wilayahStats?.desa ?? 0, icon: Home, c: 'text-blue-600', bg: 'bg-blue-50' },
    { l: 'TPS', v: wilayahStats?.tps ?? 0, icon: MapPin, c: 'text-emerald-600', bg: 'bg-emerald-50' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-sm text-orange-900">Data DPT Sumber Wilayah & Pemilih Resmi</p>
          <p className="text-xs text-orange-800 font-medium mt-1">
            Sistem kini mendukung impor langsung format file DPT dengan kolom lengkap (<code>nik, nama, tempat_lahir, tanggal_lahir, jenis_kelamin, alamat, rt, rw, desa, kecamatan, tps</code>).
            Sesuai kebijakan keamanan data (Zero-NIK), kolom di luar kebutuhan wilayah & nama akan diabaikan otomatis dan nomor NIK 16 digit asli wajib dikosongkan/diisi tanda minus (<code>-</code>).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {stats.map(s => (
          <div key={s.l} className="bg-white rounded-2xl p-5 card-shadow card-hover">
            <div className={`w-11 h-11 rounded-xl ${s.bg} flex items-center justify-center mb-3`}><s.icon className={`w-5 h-5 ${s.c}`} /></div>
            <p className="text-sm font-semibold text-gray-500">{s.l}</p>
            <h3 className="text-2xl font-extrabold text-gray-900 mt-1">{new Intl.NumberFormat('id-ID').format(s.v)}</h3>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-[300px]">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama pemilih..." className="pl-9 h-10" />
            </div>
            <Select value={filterKec} onValueChange={setFilterKec}>
              <SelectTrigger className="w-56 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kecamatan</SelectItem>
                {kecList.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleExport} disabled={exporting} className="h-10 gap-2 font-semibold">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export Data
            </Button>
            {canWrite && (
              <>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
                <Button variant="outline" onClick={downloadTemplate} className="h-10 gap-2 font-semibold"><FileSpreadsheet className="w-4 h-4" /> Template</Button>
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="h-10 gap-2 font-semibold">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import Excel
                </Button>
                <Button onClick={() => { setEditData(null); setDialogOpen(true); }} className="h-10 bg-orange-500 hover:bg-orange-600 gap-2 font-bold"><Plus className="w-4 h-4" /> Tambah</Button>
              </>
            )}
            {isSuperAdmin && total > 0 && (
              <Button variant="outline" onClick={() => setConfirmDeleteAll(true)} className="h-10 gap-2 font-semibold border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4" /> Hapus Semua
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold">Nama</TableHead>
                <TableHead className="font-extrabold">Kecamatan / Desa</TableHead>
                <TableHead className="font-extrabold">RW/RT</TableHead>
                <TableHead className="font-extrabold">TPS</TableHead>
                {canWrite && <TableHead className="font-extrabold">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400 font-semibold">Memuat data...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400 font-semibold">Belum ada data DPT. Upload lewat "Import Excel".</TableCell></TableRow>
              ) : items.map(d => (
                <TableRow key={d.id} className="hover:bg-orange-50/30">
                  <TableCell className="font-bold text-gray-900">{d.nama}</TableCell>
                  <TableCell className="text-xs font-semibold text-gray-600">{d.kecamatan}{d.desa && `, ${d.desa}`}</TableCell>
                  <TableCell className="text-xs font-semibold text-gray-600">{[d.rw, d.rt].filter(Boolean).join(' / ') || '-'}</TableCell>
                  <TableCell className="text-xs font-semibold text-gray-600">{d.tps || '-'}</TableCell>
                  {canWrite && (
                    <TableCell>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditData(d); setDialogOpen(true); }} className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-500 hover:text-orange-600"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => setConfirmDel(d)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pager page={page} pages={pages} total={total} limit={limit} onPageChange={setPage} />
        </div>
      </div>

      <EntityFormDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        title={editData ? `Edit ${editData.nama}` : 'Tambah Data DPT'}
        fields={FIELDS} initialData={editData} onSubmit={handleSubmit}
      />
      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Hapus Data DPT?" description={`Data "${confirmDel?.nama}" akan dihapus permanen.`}
        onConfirm={() => remove(confirmDel.id)}
      />
      <ConfirmDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}
        title={`Hapus SEMUA ${new Intl.NumberFormat('id-ID').format(total)} Data DPT?`}
        description="Seluruh data DPT akan dihapus permanen dan tidak bisa dikembalikan. Jumlah wilayah (Kecamatan/Desa/RW/RT/TPS) di seluruh aplikasi akan kembali memakai angka lama sampai Anda upload ulang DPT. Data Partisipan tidak ikut terhapus."
        onConfirm={handleDeleteAll}
      />
    </div>
  );
};

export default Dpt;
